// File: src/pages/RCRiskModel.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  Users,
  Target,
  CheckCircle,
  Activity,
  Eye,
  Search,
  Filter,
  BarChart3,
  PieChart,
  Clock,
  Award,
  Zap,
  Info,
  Download,
  Bell,
  X,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
} from "recharts";
import { calculateRiskManagement } from "./publicRCData/rcRiskModelCalculations";
import { useAuth } from "../hooks/AuthenticationProvider";

const RCRiskModel = () => {
  const { organizationId } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [selectedResident, setSelectedResident] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);

  const formatRecoveryTime = (days) => {
    if (!days || days === 0) return { text: "0 days", sizeClass: "text-3xl" };

    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const remainingDays = Math.floor(days % 30);

    const parts = [];
    if (years > 0) parts.push(`${years} year${years !== 1 ? "s" : ""}`);
    if (months > 0) parts.push(`${months} month${months !== 1 ? "s" : ""}`);
    if (remainingDays > 0)
      parts.push(`${remainingDays} day${remainingDays !== 1 ? "s" : ""}`);

    const text = parts.join(", ");

    // Determine size class based on complexity
    let sizeClass;
    if (parts.length === 1) {
      sizeClass = "text-3xl"; // Just days or just months or just years
    } else if (parts.length === 2) {
      sizeClass = "text-2xl"; // Two components (e.g., months and days)
    } else {
      sizeClass = "text-xl"; // All three components (years, months, days)
    }

    return { text, sizeClass };
  };

  const CravingsValidationBanner = ({ validation }) => {
    if (!validation || validation.status === "insufficient_data") return null;

    const statusColors = {
      building_dataset: "yellow",
      preliminary_validation: "blue",
      validated: "green",
    };

    const color = statusColors[validation.status] || "gray";

    return (
      <div className={`bg-${color}-900/40 border-b border-${color}-700/50`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Target className={`h-5 w-5 text-${color}-400 flex-shrink-0`} />
            <div>
              <div className={`font-semibold text-${color}-300`}>
                Cravings Predictor:{" "}
                {validation.status.replace("_", " ").toUpperCase()}
              </div>
              <div className={`text-${color}-200 text-sm`}>
                {validation.message} • {validation.action}
                {validation.oddsRatio && (
                  <> • Current odds ratio: {validation.oddsRatio.toFixed(1)}x</>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TemporalValidationBanner = ({ validation }) => {
    if (!validation || validation.status === "insufficient_data") return null;

    const getStatusColor = (status, accuracy) => {
      if (status === "validated" && accuracy > 0.7) return "green";
      if (status === "validated" && accuracy > 0.6) return "blue";
      return "yellow";
    };

    const color = getStatusColor(validation.status, validation.averageAccuracy);

    return (
      <div className={`bg-${color}-900/40 border-b border-${color}-700/50`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Clock className={`h-5 w-5 text-${color}-400 flex-shrink-0`} />
            <div>
              <div className={`font-semibold text-${color}-300`}>
                Model Validation:{" "}
                {validation.status.toUpperCase().replace("_", " ")}
              </div>
              <div className={`text-${color}-200 text-sm`}>
                {validation.averageAccuracy > 0 && (
                  <>
                    Average accuracy:{" "}
                    {(validation.averageAccuracy * 100).toFixed(1)}% across time
                    periods •{" "}
                  </>
                )}
                {validation.recommendation}
                <> • Sample: {validation.sampleSize} historical residents</>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        const data = await calculateRiskManagement(organizationId);

        // 🔍 DEBUG LOGS FOR RISK MANAGEMENT DATA
        console.log(`\n🔍 RISK MANAGEMENT DEBUG - Full Data Analysis:`);
        console.log(`Organization ID: ${organizationId}`);

        // Log overall structure
        console.log(`\nData Structure:`, {
          hasRiskStratification: !!data.riskStratification,
          hasLearnedPatterns: !!data.learnedPatterns,
          hasRcData: !!data.rcData,
          hasValidationWarnings: !!data.validationWarnings,
        });

        // Log risk scores breakdown
        if (data.riskStratification?.riskScores) {
          const allRiskScores = data.riskStratification.riskScores;
          const historicalScores = allRiskScores.filter(
            (r) => r.sourceCollection === "alumni"
          );
          const currentScores = allRiskScores.filter(
            (r) => r.sourceCollection === "residents"
          );
        }

        // Log learned patterns if available
        if (data.learnedPatterns) {
          console.log(`\n🧠 Learned Patterns:`);
          console.log(
            `  Highest Risk Period:`,
            data.learnedPatterns.metadata.highestRiskPeriod
          );
          console.log(`  RC Thresholds:`, data.learnedPatterns.rcThresholds);
          console.log(`  RC Risk Rates:`, data.learnedPatterns.rcRiskRates);
          console.log(
            `  Risk Factor Weights:`,
            Object.keys(data.learnedPatterns.riskFactorWeights || {})
          );

          // Log time-based risk patterns
          if (data.learnedPatterns.daysSoberRiskWeights) {
            console.log(`\n⏰ Days Sober Risk Patterns:`);
            data.learnedPatterns.daysSoberRiskWeights.forEach(
              (riskData, timeGroup) => {
                console.log(`  ${timeGroup}:`);
                console.log(
                  `    Relapse Rate: ${(riskData.relapseRate * 100).toFixed(
                    1
                  )}%`
                );
                console.log(`    Sample Size: ${riskData.sampleSize}`);
                console.log(
                  `    Risk Weight: ${riskData.riskWeight.toFixed(2)}`
                );
              }
            );
          }
        }

        // Focus on 181-270 days period specifically
        if (data.riskStratification?.riskScores) {
        }

        // Log validation warnings
        if (data.validationWarnings) {
          console.log(`\n⚠️ Validation Warnings:`);
          console.log(`  Small Sample: ${data.validationWarnings.smallSample}`);
          console.log(`  Sample Size: ${data.validationWarnings.sampleSize}`);
        }

        setMetrics(data);
        setError(null);
      } catch (err) {
        console.error("Error loading metrics:", err);
        setError(err.message || "Failed to load risk management data");
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      loadMetrics();
    }
  }, [organizationId]);

  const processedData = useMemo(() => {
    if (!metrics) return null;

    const allResidents = metrics.riskStratification?.riskScores || [];
    const currentResidents = allResidents.filter(
      (r) => r.sourceCollection === "residents"
    );

    // Filter by search and risk level
    const filteredResidents = currentResidents
      .filter((resident) => {
        const matchesSearch =
          !searchTerm ||
          resident.residentName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          resident.residentId?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRiskFilter =
          riskFilter === "all" || resident.riskLevel === riskFilter;

        return matchesSearch && matchesRiskFilter;
      })
      .sort((a, b) => {
        // Sort by risk score in descending order (highest risk first)
        return (b.riskScore || 0) - (a.riskScore || 0);
      });

    console.log("TOP 3 RESIDENTS FOR UI:", filteredResidents.slice(0, 3));

    const riskDistribution = {
      high: currentResidents.filter((r) => r.riskLevel === "high").length,
      medium: currentResidents.filter((r) => r.riskLevel === "medium").length,
      low: currentResidents.filter((r) => r.riskLevel === "low").length,
    };

    const highRiskResidents = currentResidents
      .filter((r) => r.riskLevel === "high")
      .sort((a, b) => b.riskScore - a.riskScore);

    const topRiskFactors = Object.entries(metrics.riskMetrics || {})
      .filter(([_, metric]) => metric && metric.or > 1.2)
      .sort((a, b) => b[1].or - a[1].or);

    return {
      allResidents,
      currentResidents,
      filteredResidents,
      riskDistribution,
      highRiskResidents,
      topRiskFactors,
      totalCurrent: currentResidents.length,
      totalHistorical: metrics.rcData?.totalUniqueIndividuals || 0,
    };
  }, [metrics, searchTerm, riskFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mx-auto mb-4"></div>
          <div className="text-white text-xl font-semibold">
            Loading Risk Analysis...
          </div>
          <div className="text-slate-400 mt-2">
            Analyzing historical patterns
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-8 max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-red-300 text-center mb-2">
            Error Loading Data
          </h3>
          <p className="text-red-200 text-center">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics || !processedData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-xl p-8 max-w-md">
          <AlertTriangle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-yellow-300 text-center mb-2">
            No Data Available
          </h3>
          <p className="text-yellow-200 text-center">
            Risk management data is not available yet. Please ensure assessments
            have been completed.
          </p>
        </div>
      </div>
    );
  }

  const StatCard = ({
    title,
    value,
    description,
    icon: Icon,
    color,
    trend,
    onClick,
    className = "",
  }) => (
    <div
      className={`bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:bg-slate-800/80 transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={`p-3 rounded-xl ${
              color.includes("red")
                ? "bg-red-500/20"
                : color.includes("yellow")
                ? "bg-yellow-500/20"
                : color.includes("green")
                ? "bg-green-500/20"
                : color.includes("blue")
                ? "bg-blue-500/20"
                : "bg-purple-500/20"
            }`}
          >
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
          <span className="text-slate-300 font-medium text-sm">{title}</span>
        </div>
        {trend && (
          <div
            className={`text-xs px-3 py-1 rounded-full font-medium ${
              trend > 0
                ? "bg-green-900/30 text-green-400"
                : "bg-red-900/30 text-red-400"
            }`}
          >
            {trend > 0 ? "+" : ""}
            {trend}%
          </div>
        )}
      </div>
      <div className={`text-3xl font-bold ${color} mb-2`}>{value}</div>
      <div className="text-sm text-slate-400">{description}</div>
    </div>
  );

  const RiskFactorChip = ({ factor, metric, rank }) => {
    if (!metric) return null;

    const isProtective = metric.or < 1;
    const impact =
      metric.or > 3
        ? "Critical"
        : metric.or > 2
        ? "High"
        : metric.or > 1.5
        ? "Moderate"
        : "Low";

    return (
      <div
        className={`relative overflow-hidden rounded-xl p-6 border ${
          isProtective
            ? "bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-700/50"
            : "bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-700/50"
        }`}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                isProtective ? "bg-green-600" : "bg-red-600"
              } text-white shadow-lg`}
            >
              #{rank}
            </div>
            <div>
              <h4
                className={`font-semibold text-lg ${
                  isProtective ? "text-green-300" : "text-red-300"
                }`}
              >
                {factor
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    metric.strength === "high"
                      ? "bg-blue-900/40 text-blue-300"
                      : metric.strength === "moderate"
                      ? "bg-yellow-900/40 text-yellow-300"
                      : "bg-gray-900/40 text-gray-300"
                  }`}
                >
                  {metric.strength} confidence
                </span>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    impact === "Critical"
                      ? "bg-red-900/40 text-red-300"
                      : impact === "High"
                      ? "bg-orange-900/40 text-orange-300"
                      : impact === "Moderate"
                      ? "bg-yellow-900/40 text-yellow-300"
                      : "bg-gray-900/40 text-gray-300"
                  }`}
                >
                  {impact} impact
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {metric.or.toFixed(1)}x
            </div>
            <div className="text-xs text-slate-400">Odds Ratio</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {metric.rateExp}%
            </div>
            <div className="text-xs text-slate-400">With Factor</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {(metric.sampleSizes?.with || 0) +
                (metric.sampleSizes?.without || 0)}
            </div>
            <div className="text-xs text-slate-400">Sample Size</div>
          </div>
        </div>

        <div className="text-xs text-slate-400">
          {isProtective ? "Protective Factor" : "Risk Factor"}: {metric.rateExp}
          % vs {metric.rateRef}% relapse rate
        </div>
      </div>
    );
  };

  const ResidentCard = ({ resident, rank, onClick }) => {
    const riskColor =
      resident.riskLevel === "high"
        ? "red"
        : resident.riskLevel === "medium"
        ? "yellow"
        : "green";
    const daysSinceAssessment = resident.lastAssessment
      ? Math.floor(
          (new Date() - new Date(resident.lastAssessment)) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

    return (
      <div
        className={`relative overflow-hidden rounded-xl p-6 border cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl ${
          resident.riskLevel === "high"
            ? "bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-700/50 hover:border-red-600/70"
            : resident.riskLevel === "medium"
            ? "bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border-yellow-700/50 hover:border-yellow-600/70"
            : "bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-700/50 hover:border-green-600/70"
        }`}
        onClick={() => onClick(resident)}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                riskColor === "red"
                  ? "bg-red-600"
                  : riskColor === "yellow"
                  ? "bg-yellow-600"
                  : "bg-green-600"
              } shadow-lg`}
            >
              #{rank}
            </div>
            <div>
              <h4
                className={`font-semibold text-lg ${
                  riskColor === "red"
                    ? "text-red-200"
                    : riskColor === "yellow"
                    ? "text-yellow-200"
                    : "text-green-200"
                }`}
              >
                {resident.residentName || `Resident ${resident.residentId}`}
              </h4>
              <div className="text-xs text-slate-400">
                ID: {resident.residentId?.slice(0, 12)}... •{" "}
                {Math.floor(resident.age || 0)} years old
              </div>
            </div>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-xs font-bold ${
              riskColor === "red"
                ? "bg-red-600/30 text-red-300 border-red-600/50"
                : riskColor === "yellow"
                ? "bg-yellow-600/30 text-yellow-300 border-yellow-600/50"
                : "bg-green-600/30 text-green-300 border-green-600/50"
            } border`}
          >
            {resident.riskLevel?.toUpperCase()} RISK
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div
              className={`text-xl font-bold mb-1 ${
                riskColor === "red"
                  ? "text-red-400"
                  : riskColor === "yellow"
                  ? "text-yellow-400"
                  : "text-green-400"
              }`}
            >
              {resident.riskScore?.toFixed(1) || "0.0"}
            </div>
            <div className="text-xs text-slate-400">Risk Score</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-blue-400 mb-1">
              {Math.round(resident.recoveryCapitalTotal || 0)}
            </div>
            <div className="text-xs text-slate-400">RC Score</div>
          </div>
          <div className="text-center">
            <div
              className={`font-bold text-purple-400 mb-1 ${(() => {
                const daysToShow = resident.daysSoberAtAssessment || 0;
                const recoveryTime = formatRecoveryTime(daysToShow);
                return recoveryTime.sizeClass === "text-3xl"
                  ? "text-xl"
                  : recoveryTime.sizeClass === "text-2xl"
                  ? "text-lg"
                  : "text-base";
              })()}`}
            >
              {(() => {
                const daysToShow = resident.daysSoberAtAssessment || 0;
                return formatRecoveryTime(daysToShow).text;
              })()}
            </div>
            <div className="text-xs text-slate-400">Recovery Time</div>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-xs text-slate-400 mb-2">Risk Factors:</div>
          <div className="flex flex-wrap gap-1">
            {(resident.riskFactors || []).map((factor, idx) => (
              <span
                key={idx}
                className={`px-2 py-1 rounded text-xs border ${
                  riskColor === "red"
                    ? "bg-red-900/40 text-red-300 border-red-700/30"
                    : riskColor === "yellow"
                    ? "bg-yellow-900/40 text-yellow-300 border-yellow-700/30"
                    : "bg-green-900/40 text-green-300 border-green-700/30"
                }`}
              >
                {factor}
              </span>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center text-xs text-slate-500">
          <span>Last assessment: {daysSinceAssessment} days ago</span>
          <span>Enrolled: {resident.daysInHouse || 0} days</span>
        </div>
      </div>
    );
  };

  const ResidentDetailModal = ({ resident, onClose }) => {
    if (!resident) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-slate-700">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {resident.residentName}
                </h2>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span>ID: {resident.residentId}</span>
                  <span>
                    Age:{" "}
                    {resident.age > 0 ? `${resident.age} years` : "Age unknown"}
                  </span>
                  <span>
                    Recovery Time:{" "}
                    {
                      formatRecoveryTime(resident.daysSoberAtAssessment || 0)
                        .text
                    }
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-slate-400" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard
                title="Risk Score"
                value={resident.riskScore?.toFixed(1) || "0.0"}
                description={`${
                  resident.riskLevel?.toUpperCase() || "UNKNOWN"
                } risk level`}
                icon={AlertTriangle}
                color="text-red-400"
              />
              <StatCard
                title="Recovery Capital"
                value={Math.round(resident.recoveryCapitalTotal || 0)}
                description="Current RC assessment"
                icon={Award}
                color="text-blue-400"
              />
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-green-500/20">
                      <Clock className="h-6 w-6 text-green-400" />
                    </div>
                    <span className="text-slate-300 font-medium text-sm">
                      Recovery Time
                    </span>
                  </div>
                </div>
                <div
                  className={`font-bold text-green-400 mb-2 ${
                    formatRecoveryTime(resident.daysSoberAtAssessment || 0)
                      .sizeClass
                  }`}
                >
                  {formatRecoveryTime(resident.daysSoberAtAssessment || 0).text}
                </div>
                <div className="text-sm text-slate-400">
                  At time of assessment
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Risk Factors
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(resident.riskFactors || []).map((factor, idx) => {
                  // Format the factor name properly
                  let displayName = factor;
                  let isProtective = factor.includes("Protective");

                  // Map raw factor names to proper display names
                  if (factor === "cod") {
                    displayName = "Co-occurring Diagnosis";
                  } else if (factor === "trauma") {
                    displayName = "Childhood Trauma";
                  } else if (factor === "prison") {
                    displayName = "Prison Experience";
                  } else if (factor === "homeless") {
                    displayName = "Housing Instability";
                  } else if (factor.includes("Strong Recovery Capital")) {
                    // Keep as is - already formatted properly
                  } else if (factor.includes("Strong Sense of Purpose")) {
                    // Keep as is - already formatted properly
                  } else if (factor.includes("Risk Period")) {
                    // Keep as is - already contains proper formatting
                  } else if (factor.includes("Recovery Capital")) {
                  } else if (factor.includes("Active Cravings")) {
                    // Keep as is - already formatted properly
                    displayName = factor;
                  } else if (factor.includes("Cravings")) {
                    displayName = "Active Substance Cravings";
                    // Keep as is - already contains proper formatting
                  } else if (factor.includes("Stability")) {
                    // Keep as is - already contains proper formatting
                  } else {
                    // For any other factors, capitalize properly
                    displayName = factor
                      .replace(/([A-Z])/g, " $1") // Add space before capitals
                      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
                      .trim();
                  }

                  return (
                    <div
                      key={idx}
                      className={`rounded-lg p-4 border ${
                        isProtective
                          ? "bg-green-900/20 border-green-700/50"
                          : "bg-slate-700/50 border-slate-600/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <AlertTriangle
                          className={`h-5 w-5 ${
                            isProtective ? "text-green-400" : "text-red-400"
                          }`}
                        />
                        <span className="text-white font-medium">
                          {displayName}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Risk Score Calculation
              </h3>
              <div className="bg-slate-700/50 rounded-lg p-6 border border-slate-600/50">
                {/* Base Risk Score Display */}
                <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-600/50">
                  <span className="text-lg font-medium text-white">
                    Total Risk Score
                  </span>
                  <span className="text-2xl font-bold text-red-400">
                    {resident.riskScore?.toFixed(1) || "0.0"}
                  </span>
                </div>

                {/* Risk Score Breakdown */}
                <div className="space-y-4">
                  <div className="text-sm font-medium text-slate-300 mb-3">
                    Score Components:
                  </div>
                  {/* Show Raw Risk Score if different from final */}
                  {resident.rawRiskScore !== undefined &&
                    resident.rawRiskScore !== resident.riskScore && (
                      <div className="flex justify-between items-center p-3 bg-slate-600/30 rounded-lg">
                        <div>
                          <span className="text-slate-300">
                            Raw Calculated Score
                          </span>
                          <div className="text-xs text-slate-400">
                            Before minimum threshold (0.1)
                          </div>
                        </div>
                        <span className="text-lg font-mono text-slate-200">
                          {resident.rawRiskScore?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                    )}
                  {/* Time-based Risk */}
                  <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                    <div>
                      <span className="text-slate-300">Days Sober Risk</span>
                      <div className="text-xs text-slate-400">
                        {resident.daysSoberAtAssessment || 0} days sober at
                        assessment
                      </div>
                    </div>
                    <span className="text-lg font-mono text-orange-400">
                      {resident.actualTimeRisk > 0 ? "+" : ""}
                      {resident.actualTimeRisk?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  {/* Recovery Capital Risk */}
                  <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                    <div>
                      <span className="text-slate-300">
                        Recovery Capital Risk
                      </span>
                      <div className="text-xs text-slate-400">
                        RC Score:{" "}
                        {Math.round(resident.recoveryCapitalTotal || 0)}
                      </div>
                    </div>
                    <span className="text-lg font-mono text-blue-400">
                      {resident.actualRcRisk > 0 ? "+" : ""}
                      {resident.actualRcRisk?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  breakdown:
                  {/* Individual Risk Factors */}
                  {resident.riskFactors && resident.riskFactors.length > 0 && (
                    <>
                      <div className="text-sm font-medium text-slate-300 mt-4 mb-2">
                        Risk Factor Analysis:
                      </div>

                      {/* Show if compound analysis was used */}
                      {resident.compoundBreakdown ? (
                        <>
                          {/* Show the exact combination found */}
                          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg mb-4">
                            <div className="text-blue-300 font-medium mb-2">
                              📊 Risk Factor Combination:{" "}
                              {resident.compoundBreakdown.combinationKey}
                            </div>
                            {resident.compoundBreakdown.empiricalData ? (
                              <div className="text-xs text-blue-200">
                                <div>
                                  • Found{" "}
                                  {
                                    resident.compoundBreakdown.empiricalData
                                      .count
                                  }{" "}
                                  residents with this exact combination
                                </div>
                                <div>
                                  • Historical relapse rate:{" "}
                                  {(
                                    resident.compoundBreakdown.empiricalData
                                      .relapseRate * 100
                                  ).toFixed(1)}
                                  %
                                </div>
                                <div>
                                  • Risk score calculated directly from your
                                  data
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-blue-200">
                                Risk score calculated using individual factors
                                with interaction effects
                              </div>
                            )}
                          </div>

                          {/* Individual Factor Base Scores from Compound Weights */}
                          <div className="mb-4">
                            <div className="text-xs text-slate-400 mb-2">
                              Individual Risk Factor Contributions:
                            </div>
                            {resident.riskFactors
                              .filter(
                                (factor) =>
                                  factor === "cod" ||
                                  factor === "trauma" ||
                                  factor === "prison" ||
                                  factor === "homeless" ||
                                  factor === "lackTwoParentHouse"
                              )
                              .map((factor, idx) => {
                                // Get the actual weight from compound breakdown if available
                                let displayName = factor;
                                let actualWeight = "Unknown";
                                let relapseRate = "Unknown";

                                if (factor === "cod")
                                  displayName = "Co-occurring Diagnosis";
                                else if (factor === "trauma")
                                  displayName = "Childhood Trauma";
                                else if (factor === "prison")
                                  displayName = "Prison Experience";
                                else if (factor === "homeless")
                                  displayName = "Housing Instability";
                                else if (factor === "lackTwoParentHouse")
                                  // ← Add this
                                  displayName = "Lack of Two Parent Household";

                                // Try to get the actual compound weight data
                                // This should come from the compoundWeights.riskWeights
                                if (
                                  resident.compoundBreakdown.individualScores &&
                                  resident.compoundBreakdown.individualScores[
                                    factor
                                  ]
                                ) {
                                  actualWeight = `+${resident.compoundBreakdown.individualScores[
                                    factor
                                  ].toFixed(2)}`;
                                }

                                return (
                                  <div
                                    key={idx}
                                    className="flex justify-between items-center p-3 bg-slate-800/30 rounded mb-2"
                                  >
                                    <div>
                                      <span className="text-slate-300 text-sm font-medium">
                                        {displayName}
                                      </span>
                                      <div className="text-xs text-slate-400">
                                        From your historical data analysis
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-lg font-mono text-orange-400">
                                        {actualWeight}
                                      </span>
                                      {relapseRate !== "Unknown" && (
                                        <div className="text-xs text-slate-500">
                                          {relapseRate}% rate
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>

                          {/* Synergistic Multiplier Effect */}
                          {resident.compoundBreakdown.synergisticMultiplier >
                            1.1 &&
                            (resident.compoundBreakdown.sampleSize >= 20 ? (
                              /* ✅ robust synergy (n ≥ 20, χ² ≥ 3.84) */
                              <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg mb-4">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-red-300 font-medium">
                                    ⚠️ Synergistic Risk Multiplier
                                  </span>
                                  <span className="text-xl font-mono text-red-400">
                                    ×
                                    {resident.compoundBreakdown.synergisticMultiplier.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                                <div className="text-xs text-red-200 space-y-1">
                                  <div>
                                    • This combination is{" "}
                                    <span className="font-semibold">
                                      more dangerous
                                    </span>{" "}
                                    than the individual factors alone
                                  </div>
                                  <div>
                                    • n ={" "}
                                    {resident.compoundBreakdown.sampleSize}, χ²
                                    = {resident.compoundBreakdown.chiSquare}
                                  </div>
                                  <div>
                                    • Your data show this combo creates{" "}
                                    <span className="font-semibold">
                                      compounding risk
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              /* 🚩 low-confidence notice (n < 20) */
                              <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg mb-4">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-yellow-300 font-medium">
                                    ⚠️ Possible Synergy – Low Confidence
                                  </span>
                                  <span className="text-xl font-mono text-yellow-400">
                                    ×
                                    {resident.compoundBreakdown.synergisticMultiplier.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                                <div className="text-xs text-yellow-200">
                                  Detected with only{" "}
                                  {resident.compoundBreakdown.sampleSize} cases.
                                  Collect more data to confirm before acting.
                                </div>
                              </div>
                            ))}

                          {/* Show base calculation vs final score */}
                          <div className="p-3 bg-slate-800/50 rounded-lg mb-4">
                            <div className="text-slate-300 font-medium mb-2">
                              Calculation Breakdown:
                            </div>
                            <div className="space-y-2 text-sm">
                              {resident.compoundBreakdown.individualScores && (
                                <div className="flex justify-between">
                                  <span className="text-slate-400">
                                    Base risk factors:
                                  </span>
                                  <span className="font-mono text-orange-400">
                                    +
                                    {Object.values(
                                      resident.compoundBreakdown
                                        .individualScores
                                    )
                                      .filter((val) => typeof val === "number")
                                      .reduce((sum, val) => sum + val, 0)
                                      .toFixed(2)}
                                  </span>
                                </div>
                              )}

                              {resident.compoundBreakdown
                                .synergisticMultiplier > 1.1 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-400">
                                    Synergy multiplier:
                                  </span>
                                  <span className="font-mono text-red-400">
                                    ×
                                    {resident.compoundBreakdown.synergisticMultiplier.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between border-t border-slate-600 pt-2">
                                <span className="text-slate-300 font-medium">
                                  Compound risk score:
                                </span>
                                <span className="font-mono text-red-400 font-bold">
                                  {(resident.riskScore || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        /* Fallback for when compound breakdown isn't available */
                        <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg mb-4">
                          <div className="text-yellow-300 font-medium mb-1">
                            ⚠️ Compound Analysis Unavailable
                          </div>
                          <div className="text-xs text-yellow-200">
                            Using basic risk factor scoring. Compound analysis
                            requires sufficient historical data.
                          </div>
                        </div>
                      )}

                      {/* Protective Factors */}
                      {resident.riskFactors.some((f) =>
                        f.includes("Protective")
                      ) && (
                        <>
                          <div className="text-xs text-slate-400 mt-4 mb-2">
                            Protective Factors:
                          </div>
                          {resident.riskFactors
                            .filter((f) => f.includes("Protective"))
                            .map((factor, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-center p-3 bg-green-900/20 rounded-lg mb-2"
                              >
                                <div>
                                  <span className="text-green-300 font-medium">
                                    {factor}
                                  </span>
                                  <div className="text-xs text-green-200">
                                    Reduces overall risk score
                                  </div>
                                </div>
                                <span className="text-lg font-mono text-green-400">
                                  Protective
                                </span>
                              </div>
                            ))}
                        </>
                      )}

                      {/* Time-based and RC factors */}
                      {resident.riskFactors.some(
                        (f) =>
                          f.includes("Recovery Capital") ||
                          f.includes("Risk Period") ||
                          f.includes("Stability")
                      ) && (
                        <>
                          <div className="text-xs text-slate-400 mt-4 mb-2">
                            Assessment-based Risk Factors:
                          </div>
                          {resident.riskFactors
                            .filter(
                              (f) =>
                                f.includes("Recovery Capital") ||
                                f.includes("Risk Period") ||
                                f.includes("Stability")
                            )
                            .map((factor, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-center p-3 bg-slate-800/30 rounded-lg mb-2"
                              >
                                <div>
                                  <span className="text-slate-300 font-medium">
                                    {factor}
                                  </span>
                                  <div className="text-xs text-slate-400">
                                    Based on current assessment data
                                  </div>
                                </div>
                                <span className="text-lg font-mono text-blue-400">
                                  Dynamic
                                </span>
                              </div>
                            ))}
                        </>
                      )}

                      {/* Debug info for development */}
                      {process.env.NODE_ENV === "development" &&
                        resident.compoundBreakdown && (
                          <details className="mt-4">
                            <summary className="text-xs text-slate-500 cursor-pointer">
                              Debug: Raw Compound Data
                            </summary>
                            <pre className="text-xs text-slate-600 mt-2 p-2 bg-black/20 rounded overflow-auto">
                              {JSON.stringify(
                                resident.compoundBreakdown,
                                null,
                                2
                              )}
                            </pre>
                          </details>
                        )}
                    </>
                  )}
                  {/* Age Factor */}
                  {resident.age && resident.age < 25 && (
                    <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                      <div>
                        <span className="text-slate-300">Age Factor</span>
                        <div className="text-xs text-slate-400">
                          Age: {Math.floor(resident.age)} years (under 25)
                        </div>
                      </div>
                      <span className="text-lg font-mono text-yellow-400">
                        +0.5
                      </span>
                    </div>
                  )}
                  {/* Explanations if available */}
                  {resident.explanations &&
                    resident.explanations.length > 0 && (
                      <>
                        <div className="text-sm font-medium text-slate-300 mt-4 mb-2">
                          Calculation Notes:
                        </div>
                        <div className="space-y-2">
                          {resident.explanations.map((explanation, idx) => (
                            <div
                              key={idx}
                              className="text-xs text-slate-400 p-2 bg-slate-600/30 rounded"
                            >
                              • {explanation}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  {/* Risk Level Thresholds */}
                  <div>
                    <div className="text-sm font-medium text-slate-300 mb-2">
                      Risk Level Thresholds (Your Organization):
                    </div>
                    <div className="text-xs text-slate-400 space-y-1">
                      {metrics?.learnedPatterns?.riskThresholds ? (
                        <>
                          <div>
                            • High Risk:{" "}
                            {metrics.learnedPatterns.riskThresholds.high.toFixed(
                              1
                            )}
                            + points
                          </div>
                          <div>
                            • Medium Risk:{" "}
                            {metrics.learnedPatterns.riskThresholds.medium.toFixed(
                              1
                            )}{" "}
                            -{" "}
                            {(
                              metrics.learnedPatterns.riskThresholds.high - 0.1
                            ).toFixed(1)}{" "}
                            points
                          </div>
                          <div>
                            • Low Risk: 0.0 -{" "}
                            {(
                              metrics.learnedPatterns.riskThresholds.medium -
                              0.1
                            ).toFixed(1)}{" "}
                            points
                          </div>
                          <div className="text-blue-300 mt-1">
                            • Based on{" "}
                            {metrics.learnedPatterns.metadata?.sampleSize ||
                              "your"}{" "}
                            historical residents
                          </div>
                        </>
                      ) : (
                        <>
                          <div>• High Risk: 3.0+ points (fallback)</div>
                          <div>• Medium Risk: 1.5 - 2.9 points (fallback)</div>
                          <div>• Low Risk: 0.0 - 1.4 points (fallback)</div>
                          <div className="text-yellow-300 mt-1">
                            • Using default thresholds - need more data for
                            custom thresholds
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Recommended Actions
              </h3>
              <div className="space-y-3">
                {(() => {
                  if (resident.riskLevel === "low") {
                    return (
                      <>
                        <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-green-300">
                                Continue Current Plan
                              </div>
                              <div className="text-sm text-green-200 mt-1">
                                Maintain current support structure and check-in
                                schedule
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Clock className="h-5 w-5 text-blue-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-blue-300">
                                Quarterly Review
                              </div>
                              <div className="text-sm text-blue-200 mt-1">
                                Schedule routine quarterly assessment to monitor
                                progress
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Users className="h-5 w-5 text-purple-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-purple-300">
                                Peer Mentoring Opportunity
                              </div>
                              <div className="text-sm text-purple-200 mt-1">
                                Consider involving as mentor for newer residents
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  } else if (resident.riskLevel === "medium") {
                    return (
                      <>
                        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Target className="h-5 w-5 text-yellow-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-yellow-300">
                                Enhanced Monitoring
                              </div>
                              <div className="text-sm text-yellow-200 mt-1">
                                Increase frequency of assessments and check-ins
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Users className="h-5 w-5 text-blue-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-blue-300">
                                Peer Support Connection
                              </div>
                              <div className="text-sm text-blue-200 mt-1">
                                Connect with successful program graduate as
                                mentor
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Activity className="h-5 w-5 text-orange-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-orange-300">
                                Weekly Check-ins
                              </div>
                              <div className="text-sm text-orange-200 mt-1">
                                Schedule weekly one-on-one meetings for support
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Bell className="h-5 w-5 text-red-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-red-300">
                                Immediate Check-in Required
                              </div>
                              <div className="text-sm text-red-200 mt-1">
                                Schedule one-on-one meeting within 24 hours
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Target className="h-5 w-5 text-yellow-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-yellow-300">
                                Enhanced Monitoring
                              </div>
                              <div className="text-sm text-yellow-200 mt-1">
                                Increase frequency of assessments and check-ins
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Users className="h-5 w-5 text-blue-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-blue-300">
                                Peer Support Connection
                              </div>
                              <div className="text-sm text-blue-200 mt-1">
                                Connect with successful program graduate as
                                mentor
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/30 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-white">
                  Risk Management Center
                </h1>
                <span className="relative inline-block group">
                  <Info size={16} className="text-slate-400 cursor-pointer" />
                  <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-[1000px] -translate-x-1/2 rounded-md bg-slate-800/95 p-4 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
                    <div className="space-y-3">
                      <div className="border-b border-slate-600 pb-2">
                        <strong className="text-blue-400 text-sm">
                          How Our Risk Assessment Works
                        </strong>
                      </div>

                      <div>
                        <strong className="text-green-400">
                          What this system does:
                        </strong>
                        <div className="mt-1 text-slate-300">
                          This tool analyzes patterns from your organization's
                          historical data to identify current residents who may
                          need extra support. It looks at{" "}
                          {processedData?.totalHistorical || "your"} past
                          residents to find common factors among those who
                          struggled.
                        </div>
                      </div>

                      <div>
                        <strong className="text-purple-400">
                          How risk scores are calculated:
                        </strong>
                        <div className="mt-1 space-y-1 text-slate-300">
                          <div>
                            • <strong>Historical Patterns:</strong> We analyze
                            what happened to residents with similar backgrounds
                          </div>
                          <div>
                            • <strong>Risk Factors:</strong> Things like trauma
                            history, co-occurring diagnoses, or housing
                            instability
                          </div>
                          <div>
                            • <strong>Recovery Capital:</strong> Your current RC
                            assessment score (higher = more protective)
                          </div>
                          <div>
                            • <strong>Time in Recovery:</strong> How long
                            someone has been sober (early recovery = higher
                            risk)
                          </div>
                          <div>
                            • <strong>Combination Effects:</strong> Some risk
                            factors are more dangerous when they appear together
                          </div>
                        </div>
                      </div>

                      <div>
                        <strong className="text-amber-400">
                          Risk level meanings:
                        </strong>
                        <div className="mt-1 space-y-1 text-slate-300">
                          <div>
                            •{" "}
                            <strong className="text-red-400">High Risk:</strong>{" "}
                            Needs immediate attention and frequent check-ins
                          </div>
                          <div>
                            •{" "}
                            <strong className="text-yellow-400">
                              Medium Risk:
                            </strong>{" "}
                            Requires enhanced monitoring and support
                          </div>
                          <div>
                            •{" "}
                            <strong className="text-green-400">
                              Low Risk:
                            </strong>{" "}
                            Standard care protocol is sufficient
                          </div>
                        </div>
                      </div>

                      <div>
                        <strong className="text-cyan-400">
                          Where the data comes from:
                        </strong>
                        <div className="mt-1 space-y-1 text-slate-300">
                          <div>
                            • <strong>Your Organization's History:</strong>{" "}
                            {processedData?.totalHistorical || "Past"} residents
                            and their outcomes
                          </div>
                          <div>
                            • <strong>Recovery Capital Assessments:</strong>{" "}
                            Current questionnaire responses
                          </div>
                          <div>
                            • <strong>Background Information:</strong> Intake
                            forms and resident profiles
                          </div>
                          <div>
                            • <strong>Real-time Updates:</strong> System learns
                            from new data as it's collected
                          </div>
                        </div>
                      </div>

                      <div>
                        <strong className="text-indigo-400">
                          System reliability:
                        </strong>
                        <div className="mt-1 space-y-1 text-slate-300">
                          {metrics?.validationWarnings?.smallSample ? (
                            <div>
                              •{" "}
                              <strong className="text-yellow-400">
                                Building Phase:
                              </strong>{" "}
                              Currently learning from{" "}
                              {metrics.validationWarnings.sampleSize} residents
                              - accuracy will improve with more data
                            </div>
                          ) : (
                            <div>
                              •{" "}
                              <strong className="text-green-400">
                                Validated:
                              </strong>{" "}
                              Based on{" "}
                              {processedData?.totalHistorical || "sufficient"}{" "}
                              historical cases with good reliability
                            </div>
                          )}
                          <div>
                            • <strong>Continuous Learning:</strong>{" "}
                            Automatically updates as new outcomes are recorded
                          </div>
                          <div>
                            • <strong>Organization-Specific:</strong> Tailored
                            to your unique population and environment
                          </div>
                        </div>
                      </div>

                      <div>
                        <strong className="text-rose-400">
                          Example calculation:
                        </strong>
                        <div className="mt-1 bg-slate-900/50 p-2 rounded text-xs">
                          <div className="text-slate-400">
                            Resident with trauma + low RC score (140) + 45 days
                            sober:
                          </div>
                          <div className="text-orange-300">
                            • Trauma history: +1.2 points (based on your data)
                          </div>
                          <div className="text-blue-300">
                            • Low RC score: +0.8 points (below your average)
                          </div>
                          <div className="text-purple-300">
                            • Early recovery period: +1.5 points (0-90 days)
                          </div>
                          <div className="text-red-300 font-medium">
                            = 3.5 total → High Risk
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-slate-600 pt-2">
                        <strong className="text-yellow-400">
                          Important to remember:
                        </strong>
                        <div className="mt-1 text-slate-300">
                          This is a <strong>support tool</strong>, not a
                          prediction. It helps identify who might benefit from
                          extra attention, but every person's recovery journey
                          is unique. Use this information alongside your
                          clinical judgment and relationship with each resident.
                        </div>
                      </div>

                      <div className="text-xs text-slate-500 italic">
                        All calculations use de-identified data and comply with
                        privacy regulations. Individual resident information is
                        protected and only accessible to authorized staff.
                      </div>
                    </div>
                  </span>
                </span>
              </div>
              <p className="text-slate-400 max-w-2xl">
                Advanced screening system analyzing{" "}
                {processedData.totalHistorical} historical residents to identify
                current high-risk patterns. Currently monitoring{" "}
                {processedData.totalCurrent} active residents.
                {metrics.validationWarnings?.smallSample &&
                  " (Pilot Phase - Building Dataset)"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export Report
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Warning Banner */}
      {metrics.validationWarnings?.smallSample && (
        <div className="bg-gradient-to-r from-yellow-900/40 to-orange-900/40 border-b border-yellow-700/50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
              <div>
                <div className="font-semibold text-yellow-300">
                  Pilot Phase - Limited Dataset
                </div>
                <div className="text-yellow-200 text-sm">
                  Screening tool based on{" "}
                  {metrics.rcData?.totalUniqueIndividuals || 0} historical
                  residents. Collect 50+ residents for statistical reliability.
                  Use as supplemental screening tool.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <CravingsValidationBanner validation={metrics.cravingsValidation} />
      <TemporalValidationBanner validation={metrics.temporalValidation} />
      {metrics.learnedPatterns && (
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border-b border-blue-700/50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div>
                <div className="font-semibold text-blue-300">
                  Data-Driven Insights from Your Organization
                </div>
                <div className="text-blue-200 text-sm">
                  {metrics.learnedPatterns.metadata.highestRiskPeriod ? (
                    <>
                      Highest risk period:{" "}
                      <strong>
                        {
                          metrics.learnedPatterns.metadata.highestRiskPeriod
                            .range
                        }
                      </strong>
                      (
                      {(
                        metrics.learnedPatterns.metadata.highestRiskPeriod
                          .relapseRate * 100
                      ).toFixed(1)}
                      % relapse rate) •{" "}
                    </>
                  ) : (
                    ""
                  )}
                  RC threshold:{" "}
                  <strong>
                    Below {metrics.learnedPatterns.rcThresholds.low}
                  </strong>{" "}
                  shows elevated risk • Found{" "}
                  <strong>
                    {
                      Object.keys(metrics.learnedPatterns.riskFactorWeights)
                        .length
                    }
                  </strong>{" "}
                  significant risk factors
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-1 flex gap-1 border border-slate-700/50">
            {[
              { id: "overview", label: "Overview", icon: Eye },
              { id: "residents", label: "Current Residents", icon: Users },
              { id: "insights", label: "Risk Insights", icon: Target },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSelectedView(id)}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 ${
                  selectedView === id
                    ? "bg-purple-600 text-white shadow-lg"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {selectedView === "overview" && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="High Risk Residents"
                value={processedData.riskDistribution.high}
                description="Require immediate attention"
                icon={AlertTriangle}
                color="text-red-400"
                onClick={() => setSelectedView("residents")}
              />
              <StatCard
                title="Medium Risk Residents"
                value={processedData.riskDistribution.medium}
                description="Enhanced monitoring needed"
                icon={Shield}
                color="text-yellow-400"
                onClick={() => setSelectedView("residents")}
              />
              <StatCard
                title="Low Risk Residents"
                value={processedData.riskDistribution.low}
                description="Standard care protocol"
                icon={CheckCircle}
                color="text-green-400"
              />
              <StatCard
                title="Risk Patterns Found"
                value={processedData.topRiskFactors.length}
                description="From historical analysis"
                icon={Target}
                color="text-blue-400"
                onClick={() => setSelectedView("insights")}
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Risk Distribution */}
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-purple-400" />
                    Current Risk Distribution
                  </h3>
                  <div className="text-xs text-slate-400">
                    {processedData.totalCurrent} active residents
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={[
                        {
                          name: "High Risk",
                          value: processedData.riskDistribution.high,
                          color: "#EF4444",
                        },
                        {
                          name: "Medium Risk",
                          value: processedData.riskDistribution.medium,
                          color: "#F59E0B",
                        },
                        {
                          name: "Low Risk",
                          value: processedData.riskDistribution.low,
                          color: "#10B981",
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {[
                        {
                          name: "High Risk",
                          value: processedData.riskDistribution.high,
                          color: "#EF4444",
                        },
                        {
                          name: "Medium Risk",
                          value: processedData.riskDistribution.medium,
                          color: "#F59E0B",
                        },
                        {
                          name: "Low Risk",
                          value: processedData.riskDistribution.low,
                          color: "#10B981",
                        },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1E293B",
                        border: "1px solid #475569",
                        borderRadius: "8px",
                        color: "#F1F5F9",
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>

              {/* Screening Performance */}
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-400" />
                  Screening Tool Status
                </h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-300">Historical Data</span>
                      <span className="text-blue-400 font-bold">
                        {processedData.totalHistorical}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-400 h-3 rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min(
                            100,
                            (processedData.totalHistorical / 100) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Target: 100+ residents for optimal reliability
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-300">
                        Risk Factors Detected
                      </span>
                      <span className="text-green-400 font-bold">
                        {processedData.topRiskFactors.length}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-green-500 to-green-400 h-3 rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min(
                            100,
                            (processedData.topRiskFactors.length / 8) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Meaningful patterns identified
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-300">Data Quality</span>
                      <span className="text-purple-400 font-bold">
                        {processedData.totalHistorical >= 50
                          ? "Good"
                          : processedData.totalHistorical >= 30
                          ? "Fair"
                          : "Building"}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-purple-400 h-3 rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min(
                            100,
                            (processedData.totalHistorical / 50) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Statistical reliability assessment
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* High-Priority Alerts */}
            {processedData.highRiskResidents.length > 0 && (
              <div className="bg-gradient-to-r from-red-900/30 to-red-800/20 border border-red-700/50 rounded-xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-red-300 flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Priority Alerts - High Risk Residents
                  </h3>
                  <button
                    onClick={() => setSelectedView("residents")}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                  >
                    View All ({processedData.highRiskResidents.length})
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {processedData.highRiskResidents
                    .slice(0, 6)
                    .map((resident, idx) => (
                      <div
                        key={resident.residentId}
                        className="bg-red-900/20 border border-red-700/30 rounded-lg p-4"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <div className="font-medium text-red-200">
                              {resident.residentName}
                            </div>
                            <div className="text-xs text-red-400">
                              Risk Score: {resident.riskScore?.toFixed(1)}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-400">
                            RC: {Math.round(resident.recoveryCapitalTotal || 0)}
                          </span>
                          <button
                            onClick={() => setSelectedResident(resident)}
                            className="text-xs text-red-300 hover:text-red-200 underline"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-400" />
                Recommended Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-red-300 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Immediate (Next 24 Hours)
                  </h4>
                  <div className="space-y-3">
                    {processedData.highRiskResidents.length > 0 && (
                      <div className="flex items-start gap-3 p-3 bg-red-900/20 rounded-lg border border-red-700/30">
                        <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-red-300">
                            Emergency Check-ins
                          </div>
                          <div className="text-sm text-red-200">
                            Schedule meetings with{" "}
                            {processedData.highRiskResidents.length} high-risk
                            residents
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-3 p-3 bg-yellow-900/20 rounded-lg border border-yellow-700/30">
                      <Target className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-yellow-300">
                          Review Risk Assessments
                        </div>
                        <div className="text-sm text-yellow-200">
                          Update assessments for residents flagged by the system
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-green-300 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Strategic (Next 1-2 Weeks)
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-blue-900/20 rounded-lg border border-blue-700/30">
                      <Users className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-blue-300">
                          Enhance Support Programs
                        </div>
                        <div className="text-sm text-blue-200">
                          Develop targeted interventions for identified risk
                          factors
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-purple-900/20 rounded-lg border border-purple-700/30">
                      <Award className="h-5 w-5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-purple-300">
                          Staff Training
                        </div>
                        <div className="text-sm text-purple-200">
                          Train team on risk factor recognition and intervention
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Residents Tab */}
        {selectedView === "residents" && (
          <div className="space-y-6">
            {/* Search and Filter Controls */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search residents by name or ID..."
                      className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    value={riskFilter}
                    onChange={(e) => setRiskFilter(e.target.value)}
                  >
                    <option value="all">All Risk Levels</option>
                    <option value="high">High Risk Only</option>
                    <option value="medium">Medium Risk Only</option>
                    <option value="low">Low Risk Only</option>
                  </select>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="p-2 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <Filter className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className="mt-4 p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Age Range
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          className="flex-1 px-3 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                        />
                        <span className="text-slate-400">-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          className="flex-1 px-3 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Days Sober
                      </label>
                      <select className="w-full px-3 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm">
                        <option value="">All</option>
                        <option value="0-30">0-30 days</option>
                        <option value="31-90">31-90 days</option>
                        <option value="91-180">91-180 days</option>
                        <option value="180+">180+ days</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        RC Score Range
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          className="flex-1 px-3 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                        />
                        <span className="text-slate-400">-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          className="flex-1 px-3 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between">
              <div className="text-slate-400">
                Showing {processedData.filteredResidents.length} of{" "}
                {processedData.totalCurrent} current residents
                {searchTerm && ` matching "${searchTerm}"`}
                {riskFilter !== "all" && ` with ${riskFilter} risk level`}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-slate-400">
                    High ({processedData.riskDistribution.high})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-slate-400">
                    Medium ({processedData.riskDistribution.medium})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-slate-400">
                    Low ({processedData.riskDistribution.low})
                  </span>
                </div>
              </div>
            </div>

            {/* Residents Grid */}
            {processedData.filteredResidents.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {processedData.filteredResidents.map((resident, idx) => (
                  <ResidentCard
                    key={resident.residentId}
                    resident={resident}
                    rank={idx + 1}
                    onClick={setSelectedResident}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-400 mb-2">
                  No Residents Found
                </h3>
                <p className="text-slate-500">
                  {searchTerm
                    ? `No residents match your search for "${searchTerm}"`
                    : "No residents match your current filters"}
                </p>
                {(searchTerm || riskFilter !== "all") && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setRiskFilter("all");
                    }}
                    className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Insights Tab */}
        {selectedView === "insights" && (
          <div className="space-y-8">
            {/* Risk Factor Analysis */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-red-400" />
                Risk Factor Impact Analysis
              </h3>

              {/* Risk Factors Chart */}
              <div className="mb-8">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={processedData.topRiskFactors.map(
                      ([factor, metric]) => ({
                        factor: factor
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase()),
                        oddsRatio: metric.or,
                        impact:
                          metric.or > 3
                            ? "Critical"
                            : metric.or > 2
                            ? "High"
                            : metric.or > 1.5
                            ? "Moderate"
                            : "Low",
                      })
                    )}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="factor"
                      tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      axisLine={{ stroke: "#374151" }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 12 }}
                      axisLine={{ stroke: "#374151" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1E293B",
                        border: "1px solid #475569",
                        borderRadius: "8px",
                        color: "#F1F5F9",
                      }}
                      formatter={(value) => [
                        `${value.toFixed(2)}x`,
                        "Odds Ratio",
                      ]}
                    />
                    <Bar
                      dataKey="oddsRatio"
                      fill="#8B5CF6"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Risk Factor Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {processedData.topRiskFactors.map(([factor, metric], idx) => (
                  <RiskFactorChip
                    key={factor}
                    factor={factor}
                    metric={metric}
                    rank={idx + 1}
                  />
                ))}
              </div>
            </div>

            {/* Historical Trends */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-400" />
                Risk Score Distribution Analysis
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="rcTotal"
                    name="Recovery Capital Score"
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    axisLine={{ stroke: "#374151" }}
                  />
                  <YAxis
                    dataKey="riskScore"
                    name="Risk Score"
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    axisLine={{ stroke: "#374151" }}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    contentStyle={{
                      backgroundColor: "#1E293B",
                      border: "1px solid #475569",
                      borderRadius: "8px",
                      color: "#F1F5F9",
                    }}
                    formatter={(value, name) => [
                      value,
                      name === "rcTotal" ? "RC Score" : "Risk Score",
                    ]}
                  />
                  <Scatter
                    name="Current Residents"
                    data={processedData.currentResidents}
                    fill="#8B5CF6"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            {/* Model Validation Results */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-400" />
                Model Validation Results
              </h3>

              {metrics.temporalValidation &&
              metrics.temporalValidation.results ? (
                <div className="space-y-6">
                  {/* Validation Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-400 mb-1">
                        {(
                          metrics.temporalValidation.averageAccuracy * 100
                        ).toFixed(1)}
                        %
                      </div>
                      <div className="text-sm text-slate-400">
                        Average Accuracy
                      </div>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-400 mb-1">
                        {metrics.temporalValidation.results.length}
                      </div>
                      <div className="text-sm text-slate-400">
                        Validation Tests
                      </div>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="text-2xl font-bold text-purple-400 mb-1">
                        {metrics.temporalValidation.sampleSize}
                      </div>
                      <div className="text-sm text-slate-400">
                        Historical Sample
                      </div>
                    </div>
                  </div>

                  {/* Detailed Results */}
                  <div>
                    <h4 className="font-medium text-slate-300 mb-4">
                      Temporal Validation Tests
                    </h4>
                    <div className="space-y-3">
                      {metrics.temporalValidation.results.map((result, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-700/30 rounded-lg p-4"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                            <div>
                              <div className="font-medium text-slate-300">
                                Test {idx + 1}
                              </div>
                              <div className="text-xs text-slate-400">
                                {Math.round(result.splitPoint * 100)}% train /{" "}
                                {Math.round((1 - result.splitPoint) * 100)}%
                                test
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-slate-300">
                                Training: {result.trainPeriod.start} to{" "}
                                {result.trainPeriod.end}
                              </div>
                              <div className="text-xs text-slate-400">
                                {result.trainSize} residents
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-slate-300">
                                Testing: {result.testPeriod.start} to{" "}
                                {result.testPeriod.end}
                              </div>
                              <div className="text-xs text-slate-400">
                                {result.testSize} residents
                              </div>
                            </div>
                            <div className="text-right">
                              <div
                                className={`text-lg font-bold ${
                                  result.accuracy > 0.7
                                    ? "text-green-400"
                                    : result.accuracy > 0.6
                                    ? "text-blue-400"
                                    : "text-yellow-400"
                                }`}
                              >
                                {(result.accuracy * 100).toFixed(1)}%
                              </div>
                              <div className="text-xs text-slate-400">
                                {result.correctPredictions}/
                                {result.totalPredictions} correct
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Interpretation */}
                  <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                    <div className="font-medium text-blue-300 mb-2">
                      Validation Interpretation:
                    </div>
                    <div className="text-sm text-blue-200">
                      {metrics.temporalValidation.recommendation}. This temporal
                      validation tests whether patterns from earlier residents
                      can predict outcomes for later residents, proving the
                      model works in real-world conditions as your organization
                      evolves over time.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <div className="text-slate-400 mb-2">
                    Temporal Validation Unavailable
                  </div>
                  <div className="text-sm text-slate-500">
                    Need 20+ historical residents with outcomes for temporal
                    validation
                  </div>
                </div>
              )}
            </div>
            {/* Technical Details */}
            <div className="bg-gradient-to-r from-slate-800/60 to-slate-700/50 border border-slate-600/50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Info className="h-5 w-5 text-slate-400" />
                Technical Implementation
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm">
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-300 mb-2">
                    Screening Method
                  </h4>
                  <div className="space-y-2 text-slate-400">
                    <div>• Historical pattern matching</div>
                    <div>• Risk factor identification</div>
                    <div>• Statistical significance testing</div>
                    <div>• Recovery Capital integration</div>
                    <div>• Days sober correlation</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-slate-300 mb-2">
                    Data Sources
                  </h4>
                  <div className="space-y-2 text-slate-400">
                    <div>
                      • Historical residents: {processedData.totalHistorical}
                    </div>
                    <div>
                      • Risk factors: {processedData.topRiskFactors.length}{" "}
                      identified
                    </div>
                    <div>
                      • Current monitoring: {processedData.totalCurrent} active
                    </div>
                    <div>• Update frequency: Real-time</div>
                    <div>
                      • Data quality:{" "}
                      {processedData.totalHistorical >= 50
                        ? "Good"
                        : "Building"}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-slate-300 mb-2">
                    Current Status
                  </h4>
                  <div className="space-y-2 text-slate-400">
                    <div>
                      • Status:{" "}
                      {metrics.validationWarnings?.smallSample
                        ? "Pilot Phase"
                        : "Production Ready"}
                    </div>
                    <div>
                      • Reliability:{" "}
                      {processedData.totalHistorical >= 50
                        ? "High"
                        : "Building"}
                    </div>
                    <div>• Screening: Active for all residents</div>
                    <div>
                      • Alerts: {alertsEnabled ? "Enabled" : "Disabled"}
                    </div>
                    <div>
                      • Next milestone:{" "}
                      {processedData.totalHistorical >= 50
                        ? "Validation study"
                        : `${
                            50 - processedData.totalHistorical
                          } more residents`}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-slate-900/50 rounded-lg">
                <div className="text-xs text-slate-500 leading-relaxed">
                  <strong className="text-slate-400">Methodology:</strong> This
                  screening system identifies current residents who share
                  characteristics with historical residents who struggled at
                  discharge. Risk scores are calculated using evidence-based
                  factors including days sober at assessment, Recovery Capital
                  scores, and identified risk factors. The system continuously
                  learns from new data and will transition to validated
                  predictive modeling once adequate sample sizes are achieved
                  (50+ historical residents with outcomes).
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Resident Detail Modal */}
      <ResidentDetailModal
        resident={selectedResident}
        onClose={() => setSelectedResident(null)}
      />
    </div>
  );
};

export default RCRiskModel;
